import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { formatDate, formatTime } from '@/utils/displayDateTime';
import { ClampText } from '@/components/ui';
import { useSearchParams } from 'react-router-dom';
import FiltersCard from '../../../components/FiltersCard';
import { MultiSelectDropdown } from '../../../components/MultiSelectDropdown';
import ServerPagination from '../../../components/ServerPagination';
import { StandoutDropdown } from '../../../components/StandoutDropdown';
import { Alert, Button, DataTable, EmptyValue, FilterField, FilterGrid, Form, ListPage, PageHeader, StatusBadge } from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { materialLedgerService } from '../../../services/materialLedgerService';
import ExportLedgerModal from '../components/ExportLedgerModal';
import MaterialsSubnav from '../components/MaterialsSubnav';
import { getSites } from '../data';
import { isStockSite, materialOptions, type MaterialOption } from '../filters';
import {
  activeLedgerFilterCount,
  binOf,
  directionFor,
  EMPTY_LEDGER_FILTERS,
  filtersFromParams,
  LEDGER_SOURCE_LABEL,
  LEDGER_SOURCES,
  LEDGER_TYPE,
  LEDGER_TYPES,
  paramsFromFilters,
  siteLabel,
  type LedgerBinRef,
  type LedgerEntry,
  type LedgerFilters,
  type LedgerPage as LedgerPageData,
} from '../ledger';
import type { Site } from '../types';
import { formatCost, formatQuantity } from '../utils/adjustInventory';

const PAGE_SIZE_DEFAULT = 50;
/** A post recorded this long after it happened (an offline sync, a late entry) shows when it was recorded. */
const LATE_RECORDING_MS = 60 * 60 * 1000;


type Load = { status: 'loading' } | { status: 'error'; message: string } | { status: 'ready'; data: LedgerPageData };

const BinCell: React.FC<{ bin: LedgerBinRef | null; showSite: boolean }> = ({ bin, showSite }) =>
  bin ? (
    <div className="tw-min-w-0">
      <p className="tw-mb-0 tw-truncate tw-text-content">
        <span className="tw-font-medium">{bin.binCode}</span>
        <span className="tw-text-content-secondary"> · {bin.locationName}</span>
      </p>
      {showSite ? <p className="tw-mb-0 tw-truncate tw-text-xs tw-text-content-muted">{siteLabel(bin)}</p> : null}
    </div>
  ) : (
    <EmptyValue />
  );

function quantityText(entry: LedgerEntry, binId: string): string {
  const amount = formatQuantity(entry.quantity, entry.unitOfMeasure);
  const direction = directionFor(entry, binId);
  if (entry.type === 'COUNT') return `= ${amount}`;
  if (direction === 'in') return `+${amount}`;
  if (direction === 'out') return `−${amount}`;
  return amount;
}

/** The material ledger (MT-V04): every movement newest first, filterable, exportable as CSV. */
const LedgerPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const applied = useMemo(() => filtersFromParams(searchParams), [searchParams]);
  const page = Math.max(1, Number(searchParams.get('page')) || 1);
  const [pageSize, setPageSize] = useState(PAGE_SIZE_DEFAULT);
  const [pending, setPending] = useState<LedgerFilters>(applied);
  const [load, setLoad] = useState<Load>({ status: 'loading' });
  const [sites, setSites] = useState<Site[]>([]);
  const [exportOpen, setExportOpen] = useState(false);

  // The URL is the source of truth; a link or Back resets what the card shows.
  useEffect(() => setPending(applied), [applied]);

  useEffect(() => {
    getSites()
      .then((all) => setSites(all.filter(isStockSite)))
      .catch(() => setSites([]));
  }, []);

  const loadPage = useCallback(async () => {
    setLoad({ status: 'loading' });
    try {
      setLoad({ status: 'ready', data: await materialLedgerService.list(applied, page, pageSize) });
    } catch (error) {
      setLoad({ status: 'error', message: error instanceof Error ? error.message : 'Failed to load the ledger' });
    }
  }, [applied, page, pageSize]);

  useEffect(() => {
    void loadPage();
  }, [loadPage]);

  const apply = (filters: LedgerFilters, nextPage = 1) => setSearchParams(paramsFromFilters(filters, nextPage));

  const siteOptions = useMemo(() => [...sites].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })), [sites]);
  const materials: MaterialOption[] = useMemo(() => materialOptions(sites), [sites]);
  const data = load.status === 'ready' ? load.data : null;
  const entries = data?.entries ?? [];
  const bin = applied.binId ? binOf(entries, applied.binId) : null;
  const oneSite = !!applied.siteId || !!applied.binId;
  const count = activeLedgerFilterCount(applied);
  const set = <K extends keyof LedgerFilters>(key: K, value: LedgerFilters[K]) => setPending((current) => ({ ...current, [key]: value }));

  const columns: DataTableColumn<LedgerEntry>[] = [
    {
      key: 'when',
      header: 'When',
      mobile: 'subtitle',
      className: 'tw-whitespace-nowrap',
      cell: (entry) => {
        const occurred = new Date(entry.occurredAt);
        const recorded = new Date(entry.createdAt);
        const late = recorded.getTime() - occurred.getTime() > LATE_RECORDING_MS;
        return (
          <div>
            <p className="tw-mb-0 tw-text-content">
              <span className="tw-whitespace-nowrap">{formatDate(occurred)}</span> <span className="tw-whitespace-nowrap tw-tabular-nums tw-text-content-secondary">{formatTime(occurred)}</span>
            </p>
            {late ? <p className="tw-mb-0 tw-text-xs tw-text-content-muted">Recorded {formatDate(recorded)}</p> : null}
          </div>
        );
      },
    },
    {
      key: 'type',
      header: 'Type',
      mobile: 'title',
      cell: (entry) => (
        <div className="tw-flex tw-flex-col tw-items-start tw-gap-1">
          <StatusBadge status={entry.type} variant={LEDGER_TYPE[entry.type].variant} label={LEDGER_TYPE[entry.type].label} soft pill />
          {entry.flaggedNegative ? <StatusBadge status="negative" variant="danger" label="Below zero" soft pill /> : null}
        </div>
      ),
    },
    {
      key: 'material',
      header: 'Material',
      className: 'tw-min-w-[13rem]',
      cell: (entry) => (
        <ClampText title={entry.material.label}>
          {entry.material.label}
        </ClampText>
      ),
    },
    {
      key: 'quantity',
      header: 'Quantity',
      mobile: 'trailing',
      align: 'right',
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: (entry) => {
        const direction = directionFor(entry, applied.binId);
        return (
          <div>
            <p className={`tw-mb-0 tw-font-medium ${direction === 'out' ? 'tw-text-danger' : direction === 'in' ? 'tw-text-success' : 'tw-text-content'}`}>
              {quantityText(entry, applied.binId)}
            </p>
            {entry.type === 'COUNT' && entry.expectedQuantity !== null ? (
              <p className="tw-mb-0 tw-text-xs tw-text-content-muted">expected {formatQuantity(entry.expectedQuantity, entry.unitOfMeasure)}</p>
            ) : null}
          </div>
        );
      },
    },
    {
      key: 'balance',
      header: 'Balance After',
      mobileLabel: 'Balance After',
      align: 'right',
      hidden: !applied.binId,
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: (entry) =>
        entry.balanceAfter === null ? (
          <EmptyValue />
        ) : (
          <span className={entry.balanceAfter < 0 ? 'tw-font-semibold tw-text-danger' : 'tw-text-content'}>
            {formatQuantity(entry.balanceAfter, entry.unitOfMeasure)}
          </span>
        ),
    },
    {
      key: 'from',
      header: 'From',
      mobileLabel: 'From',
      className: 'tw-min-w-[11rem] tw-max-w-[16rem]',
      cell: (entry) => <BinCell bin={entry.from} showSite={!oneSite || entry.type === 'TRANSFER'} />,
    },
    {
      key: 'to',
      header: 'To',
      mobileLabel: 'To',
      className: 'tw-min-w-[11rem] tw-max-w-[16rem]',
      cell: (entry) => <BinCell bin={entry.to} showSite={!oneSite || entry.type === 'TRANSFER'} />,
    },
    {
      key: 'reason',
      header: 'Reason',
      mobileLabel: 'Reason',
      className: 'tw-min-w-[14rem] tw-max-w-[22rem]',
      cell: (entry) => (
        <div>
          <ClampText as="div" className="tw-mb-0 tw-text-content" title={entry.reason}>
            {entry.reason}
          </ClampText>
          {entry.task || entry.vendor ? (
            <p className="tw-mb-0 tw-truncate tw-text-xs tw-text-content-muted">
              {[entry.task ? `Task ${entry.task.label}` : null, entry.vendor].filter(Boolean).join(' · ')}
            </p>
          ) : null}
        </div>
      ),
    },
    {
      key: 'source',
      header: 'Source',
      mobileLabel: 'Source',
      className: 'tw-whitespace-nowrap',
      cell: (entry) => (
        <div>
          <p className="tw-mb-0 tw-text-content">{LEDGER_SOURCE_LABEL[entry.source]}</p>
          {entry.recordedBy ? <p className="tw-mb-0 tw-text-xs tw-text-content-muted">{entry.recordedBy}</p> : null}
        </div>
      ),
    },
    {
      key: 'cost',
      header: 'Unit Cost',
      mobileLabel: 'Unit Cost',
      align: 'right',
      hidden: !data?.canSeeCost,
      className: 'tw-whitespace-nowrap tw-tabular-nums',
      cell: (entry) => (
        <div>
          <p className="tw-mb-0 tw-text-content">{formatCost(entry.unitCost)}</p>
          {entry.type === 'TRANSFER' && entry.destinationUnitCost !== null ? (
            <p className="tw-mb-0 tw-text-xs tw-text-content-muted">at destination {formatCost(entry.destinationUnitCost)}</p>
          ) : null}
          {entry.costCarried ? <StatusBadge status="cost-carried" variant="warning" label="Cost carried" soft pill /> : null}
        </div>
      ),
    },
  ];

  return (
    <ListPage>
      <PageHeader
        title="Material Locations"
        subtitle="Every material movement: receipts, use, transfers, counts and disposals, newest first."
        actions={[
          { key: 'refresh', label: 'Refresh', icon: 'bi-arrow-clockwise', intent: 'neutral', overflow: true, onClick: () => void loadPage() },
          { key: 'export', label: 'Export CSV', icon: 'bi-download', intent: 'neutral', onClick: () => setExportOpen(true) },
        ]}
      />

      <MaterialsSubnav />

      <FiltersCard
        title="Filters"
        collapsible
        showClearButton
        onClear={() => apply(EMPTY_LEDGER_FILTERS)}
        hasActiveFilters={count > 0}
        activeFilterCount={count}
        showApplyButton
        onApply={() => apply(pending)}
        applyDisabled={paramsFromFilters(pending).toString() === paramsFromFilters(applied).toString()}
      >
        <FilterGrid>
          <FilterField>
            {/* Label and control straight in the field, no Form.Group: its bottom margin lifted
                these above the dropdowns beside them. */}
            <Form.Label htmlFor="ledger-from">From</Form.Label>
            <Form.Control id="ledger-from" type="date" value={pending.from} max={pending.to || undefined} onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('from', event.target.value)} />
          </FilterField>
          <FilterField>
            <Form.Label htmlFor="ledger-to">To</Form.Label>
            <Form.Control id="ledger-to" type="date" value={pending.to} min={pending.from || undefined} onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('to', event.target.value)} />
          </FilterField>
          <FilterField>
            <StandoutDropdown
              type="searchable"
              label="Site"
              options={siteOptions}
              value={pending.siteId}
              // Choosing another site drops a bin filter from the old one.
              onChange={(value) => setPending((current) => ({ ...current, siteId: value || '', binId: value === current.siteId ? current.binId : '' }))}
              getValue={(site: Site) => site.id}
              formatOption={(site: Site) => (site.number ? `${site.name} #${site.number}` : site.name)}
              placeholder="All Sites"
              allowClear
            />
          </FilterField>
          <FilterField span={2}>
            <StandoutDropdown
              type="searchable"
              label="Material"
              options={materials}
              value={pending.materialId}
              onChange={(value) => set('materialId', value || '')}
              getValue={(option: MaterialOption) => option.id}
              formatOption={(option: MaterialOption) => option.label}
              placeholder="Any Material"
              allowClear
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown
              label="Type"
              options={LEDGER_TYPES}
              value={pending.types}
              onChange={(values) => set('types', values as LedgerFilters['types'])}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="All Types"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>
          <FilterField>
            <MultiSelectDropdown
              label="Source"
              options={LEDGER_SOURCES}
              value={pending.sources}
              onChange={(values) => set('sources', values as LedgerFilters['sources'])}
              getValue={(option) => option.value}
              formatOption={(option) => option.label}
              placeholder="All Sources"
              preserveOptionsOrder
              showSearch={false}
            />
          </FilterField>
          <FilterField>
            <Form.Label htmlFor="ledger-flagged">Below zero</Form.Label>
            <Form.Check
              type="switch"
              id="ledger-flagged"
              className="tw-mb-0 tw-flex tw-h-9 tw-items-center tw-gap-2"
              label="Only These"
              title="Only movements that took a bin below zero"
              checked={pending.flaggedOnly}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => set('flaggedOnly', event.target.checked)}
            />
          </FilterField>
        </FilterGrid>
      </FiltersCard>

      {applied.binId ? (
        <div className="tw-mb-2 tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-text-sm">
          <span className="tw-text-content-secondary">
            History of bin{' '}
            <span className="tw-font-medium tw-text-content">{bin ? `${bin.binCode} · ${bin.locationName}` : applied.binId}</span>
            {bin ? <span className="tw-text-content-muted"> at {siteLabel(bin)}</span> : null}, with the balance after each movement.
          </span>
          <Button variant="link" size="sm" className="tw-p-0" onClick={() => apply({ ...applied, binId: '' })}>
            {applied.siteId ? 'Show the whole site' : 'Show every bin'}
          </Button>
        </div>
      ) : data ? (
        <p className="tw-mb-2 tw-text-sm tw-text-content-muted" aria-live="polite">
          {data.total.toLocaleString('en-CA')} {data.total === 1 ? 'movement' : 'movements'}
        </p>
      ) : null}

      <ListPage.Body>
        {load.status === 'error' ? (
          <Alert variant="danger">
            <p className="tw-mb-2">{load.message}</p>
            <Button size="sm" variant="outline-danger" onClick={() => void loadPage()}>
              Try again
            </Button>
          </Alert>
        ) : (
          <DataTable
            fill
            columns={columns}
            data={entries}
            rowKey={(entry) => entry.id}
            loading={load.status === 'loading'}
            emptyMessage={count ? 'No movements match these filters.' : 'No movements recorded yet.'}
          />
        )}
      </ListPage.Body>

      {data && data.total > 0 ? (
        <ServerPagination
          currentPage={data.page}
          totalPages={Math.max(1, Math.ceil(data.total / data.pageSize))}
          totalCount={data.total}
          pageSize={data.pageSize}
          onPageChange={(next) => apply(applied, next)}
          onPageSizeChange={(size) => {
            setPageSize(size);
            apply(applied, 1);
          }}
          loading={load.status === 'loading'}
        />
      ) : null}

      <ExportLedgerModal show={exportOpen} filters={applied} onHide={() => setExportOpen(false)} />
    </ListPage>
  );
};

export default LedgerPage;
